import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/config';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const PROPERTY_TYPES = [
  { value: 'apartment', label: 'Appartamento' },
  { value: 'villa', label: 'Villa' },
  { value: 'house', label: 'Casa' },
  { value: 'room', label: 'Stanza' },
  { value: 'bb', label: 'B&B' },
];

const EMPTY = {
  name: '', property_type: 'apartment', address: '', city: '', province: '', region: '', postal_code: '',
  bedrooms: 1, bathrooms: 1, max_guests: 2,
  price_per_night: '', cleaning_cost: 0, linen_cost_per_person: 0,
  cin: '', cir: '', description: '',
  alloggiati_enabled: false, alloggiati_username: '', alloggiati_password: '', alloggiati_wskey: '',
  alloggiati_structure_code: '', alloggiati_beds_count: '',
  has_ross1000_delegation: false, ross1000_username: '', ross1000_password: '', ross1000_structure_code: '',
  home_smart_property_role: '', owner_name: '', owner_email: '', owner_phone: '', owner_tax_code: '',
  owner_address: '', owner_notes: '',
};

const Field = ({ label, help, helpTestId, children, className = '' }) => (
  <div className={className}>
    <Label className="text-xs text-slate-500">{label}</Label>
    {children}
    {help && <p className="mt-1.5 text-[11px] leading-4 text-slate-500" data-testid={helpTestId}>{help}</p>}
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 className="text-sm font-bold text-slate-700 border-b pb-1 mt-2">{children}</h3>
);

export default function PropertyFormModal({ open, onClose, property, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!property?.id;

  useEffect(() => {
    if (open) {
      if (property) {
        const f = { ...EMPTY };
        Object.keys(EMPTY).forEach((k) => {
          if (property[k] !== undefined && property[k] !== null) f[k] = property[k];
        });
        setForm(f);
      } else {
        setForm(EMPTY);
      }
    }
  }, [open, property]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setNum = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Il nome dell\'immobile è obbligatorio'); return; }
    if (!['proprietario', 'gestore'].includes(form.home_smart_property_role)) { toast.error('Indica se sei proprietario o gestore'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('hw_token');
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        ...form,
        bedrooms: parseInt(form.bedrooms) || 1,
        bathrooms: parseInt(form.bathrooms) || 1,
        max_guests: parseInt(form.max_guests) || 2,
        price_per_night: form.price_per_night === '' ? null : parseFloat(form.price_per_night),
        cleaning_cost: parseFloat(form.cleaning_cost) || 0,
        linen_cost_per_person: parseFloat(form.linen_cost_per_person) || 0,
        alloggiati_beds_count: form.alloggiati_beds_count === '' ? null : parseInt(form.alloggiati_beds_count),
      };
      if (isEdit) {
        await axios.put(`${API}/api/properties/${property.id}`, payload, { headers });
        toast.success('Scheda immobile aggiornata');
      } else {
        payload.property_code = `MPP${Date.now().toString(36).toUpperCase().slice(-6)}`;
        await axios.post(`${API}/api/properties`, payload, { headers });
        toast.success('Immobile creato');
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="property-form-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Modifica scheda — ${property?.name}` : 'Nuovo Immobile'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <SectionTitle>Dati principali</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Nome immobile *" className="md:col-span-2">
              <Input value={form.name} onChange={set('name')} data-testid="prop-name-input" />
            </Field>
            <Field label="Tipologia">
              <Select value={form.property_type} onValueChange={set('property_type')}>
                <SelectTrigger data-testid="prop-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Indirizzo" className="md:col-span-2">
              <Input value={form.address} onChange={set('address')} data-testid="prop-address-input" />
            </Field>
            <Field label="Città">
              <Input value={form.city} onChange={set('city')} data-testid="prop-city-input" />
            </Field>
            <Field label="Provincia">
              <Input value={form.province} onChange={set('province')} data-testid="prop-province-input" />
            </Field>
            <Field label="Regione">
              <Input value={form.region} onChange={set('region')} data-testid="prop-region-input" />
            </Field>
            <Field label="CAP">
              <Input value={form.postal_code} onChange={set('postal_code')} data-testid="prop-postal-code-input" />
            </Field>
          </div>

          <SectionTitle>Rapporto con l’immobile</SectionTitle>
          <div className="space-y-3 border border-blue-100 bg-blue-50/60 p-4" data-testid="property-role-section">
            <Field label="Il tuo ruolo per questo immobile *" help="Se gestisci l’immobile per conto di terzi, potrai scegliere quali dati del proprietario inserire e mostrare nel PDF." helpTestId="property-role-help">
              <Select value={form.home_smart_property_role} onValueChange={set('home_smart_property_role')}>
                <SelectTrigger data-testid="prop-role-select"><SelectValue placeholder="Seleziona proprietario o gestore" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="proprietario" data-testid="prop-role-owner-option">Sono il proprietario</SelectItem>
                  <SelectItem value="gestore" data-testid="prop-role-manager-option">Sono il gestore</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {form.home_smart_property_role === 'gestore' && (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="property-owner-optional-fields">
                <Field label="Nome / ragione sociale proprietario"><Input value={form.owner_name} onChange={set('owner_name')} data-testid="prop-owner-name-input" /></Field>
                <Field label="Email proprietario"><Input type="email" value={form.owner_email} onChange={set('owner_email')} data-testid="prop-owner-email-input" /></Field>
                <Field label="Telefono proprietario"><Input value={form.owner_phone} onChange={set('owner_phone')} data-testid="prop-owner-phone-input" /></Field>
                <Field label="Codice fiscale / P. IVA"><Input value={form.owner_tax_code} onChange={set('owner_tax_code')} data-testid="prop-owner-tax-code-input" /></Field>
                <Field label="Indirizzo proprietario" className="md:col-span-2"><Input value={form.owner_address} onChange={set('owner_address')} data-testid="prop-owner-address-input" /></Field>
                <Field label="Note da includere nel report" className="md:col-span-2"><Textarea rows={2} value={form.owner_notes} onChange={set('owner_notes')} data-testid="prop-owner-notes-input" /></Field>
              </div>
            )}
          </div>

          <SectionTitle>Capacità e costi</SectionTitle>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            <Field label="Camere"><Input type="number" min="0" value={form.bedrooms} onChange={setNum('bedrooms')} data-testid="prop-bedrooms-input" /></Field>
            <Field label="Bagni"><Input type="number" min="0" value={form.bathrooms} onChange={setNum('bathrooms')} data-testid="prop-bathrooms-input" /></Field>
            <Field label="Ospiti max"><Input type="number" min="1" value={form.max_guests} onChange={setNum('max_guests')} data-testid="prop-guests-input" /></Field>
            <Field label="€/notte"><Input type="number" min="0" step="0.01" value={form.price_per_night} onChange={setNum('price_per_night')} data-testid="prop-nightly-price-input" /></Field>
            <Field label="Pulizia €"><Input type="number" min="0" step="0.01" value={form.cleaning_cost} onChange={setNum('cleaning_cost')} data-testid="prop-cleaning-cost-input" /></Field>
            <Field label="Biancheria €/pers."><Input type="number" min="0" step="0.01" value={form.linen_cost_per_person} onChange={setNum('linen_cost_per_person')} data-testid="prop-linen-cost-input" /></Field>
          </div>

          <SectionTitle>Codici identificativi</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            <Field label="CIN (Codice Identificativo Nazionale)" help="Lo trovi nella Banca Dati Strutture Ricettive (BDSR) del Ministero del Turismo dopo aver associato la struttura." helpTestId="prop-cin-help"><Input value={form.cin} onChange={set('cin')} data-testid="prop-cin-input" /></Field>
            <Field label="CIR (Codice Identificativo Regionale)" help="È rilasciato da Regione o Comune dopo la registrazione della struttura; consulta il portale turistico regionale." helpTestId="prop-cir-help"><Input value={form.cir} onChange={set('cir')} data-testid="prop-cir-input" /></Field>
          </div>

          <Field label="Descrizione">
            <Textarea rows={3} value={form.description} onChange={set('description')} data-testid="prop-description-input" />
          </Field>

          <SectionTitle>Questura — Alloggiati Web</SectionTitle>
          <p className="text-xs leading-5 text-slate-600" data-testid="questura-credentials-intro">Per ottenere l’utenza devi presentare la richiesta alla Questura competente. Dopo l’abilitazione accedi al <a href="https://alloggiatiweb.poliziadistato.it" target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-700 underline" data-testid="alloggiati-official-portal-link">portale ufficiale Alloggiati Web</a>.</p>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.alloggiati_enabled}
                   onChange={(e) => setForm((f) => ({ ...f, alloggiati_enabled: e.target.checked }))}
                   data-testid="prop-alloggiati-enabled" />
            Invio schedine Questura attivo per questo immobile
          </label>
          {form.alloggiati_enabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 rounded-lg p-4" data-testid="questura-credentials-fields">
              <Field label="Username" help="È il codice utenza rilasciato dalla Questura e usato per accedere ad Alloggiati Web." helpTestId="questura-username-help"><Input value={form.alloggiati_username} onChange={set('alloggiati_username')} autoComplete="off" data-testid="prop-alloggiati-username-input" /></Field>
              <Field label="Password" help="Usa la password attuale del portale. Dopo ogni cambio password devi generare una nuova WSKEY." helpTestId="questura-password-help"><Input type="password" value={form.alloggiati_password} onChange={set('alloggiati_password')} autoComplete="new-password" data-testid="prop-alloggiati-password-input" /></Field>
              <Field label="WSKEY" help={<>Accedi al portale → menu del tuo utente in alto a destra → <b>Chiave Web Service</b> → <b>Genera nuovo codice</b>. Il portale consente una nuova chiave al giorno.</>} helpTestId="questura-wskey-help"><Input value={form.alloggiati_wskey} onChange={set('alloggiati_wskey')} autoComplete="off" data-testid="prop-alloggiati-wskey-input" /></Field>
              <Field label="Codice struttura" help="Di norma coincide con il codice utenza assegnato dalla Questura. Non inserire CIN o CIR salvo diversa indicazione dell’ufficio." helpTestId="questura-structure-code-help"><Input value={form.alloggiati_structure_code} onChange={set('alloggiati_structure_code')} data-testid="prop-alloggiati-structure-code-input" /></Field>
              <Field label="N. posti letto" help="Inserisci i posti letto autorizzati indicati nella SCIA o nell’autorizzazione della struttura." helpTestId="questura-beds-help"><Input type="number" min="0" value={form.alloggiati_beds_count} onChange={setNum('alloggiati_beds_count')} data-testid="prop-alloggiati-beds-input" /></Field>
            </div>
          )}

          <SectionTitle>ISTAT — ROSS1000</SectionTitle>
          <p className="text-xs leading-5 text-slate-600" data-testid="ross1000-credentials-intro">Le credenziali sono rilasciate dal portale dei flussi turistici della tua Regione o Provincia e sono indipendenti da Alloggiati Web. Se entri con SPID, può essere necessario creare o richiedere credenziali specifiche per il Web Service.</p>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.has_ross1000_delegation}
                   onChange={(e) => setForm((f) => ({ ...f, has_ross1000_delegation: e.target.checked }))}
                   data-testid="prop-ross-enabled" />
            Invio flussi ISTAT attivo (credenziali portale regionale)
          </label>
          {form.has_ross1000_delegation && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 rounded-lg p-4" data-testid="ross1000-credentials-fields">
              <Field label="Username" help="Controlla l’email di attivazione o la sezione Profilo del portale regionale. Per il Web Service può essere diverso dall’accesso SPID." helpTestId="ross1000-username-help"><Input value={form.ross1000_username} onChange={set('ross1000_username')} autoComplete="off" data-testid="prop-ross1000-username-input" /></Field>
              <Field label="Password" help="Inserisci la password dedicata al Web Service. Se usi SPID/CIE, creala o reimpostala dalla pagina di accesso del portale regionale." helpTestId="ross1000-password-help"><Input type="password" value={form.ross1000_password} onChange={set('ross1000_password')} autoComplete="new-password" data-testid="prop-ross1000-password-input" /></Field>
              <Field label="Codice struttura" help="Lo trovi in Anagrafica o Gestione strutture del portale regionale. Usa il codice indicato per il Web Service; non sostituirlo automaticamente con CIN o CIR." helpTestId="ross1000-structure-code-help"><Input value={form.ross1000_structure_code} onChange={set('ross1000_structure_code')} data-testid="prop-ross1000-structure-code-input" /></Field>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={onClose} disabled={saving} data-testid="prop-cancel-btn">Annulla</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="prop-save-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEdit ? 'Salva modifiche' : 'Crea immobile'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
